import { useEffect, useState, useCallback } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Plus, Pencil, Eye, Send, Trash2, Check, X, CalendarDays, Users } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { StatusBadge, Empty } from "@/components/common";
import { useLang } from "@/lib/i18n";
import { useAuth } from "@/context/AuthContext";
import { api, eur, errMsg, photoUrl, fmtDate } from "@/lib/api";
import { toast } from "sonner";

export const Stat = ({ label, value, accent, testid }) => (
  <div className="rounded-2xl bg-white border border-slate-200 p-4 sm:p-5" data-testid={testid}>
    <p className="text-[11px] font-bold uppercase tracking-[0.14em] text-slate-500">{label}</p>
    <p className={`mt-1 font-display text-2xl sm:text-3xl font-extrabold ${accent ? "text-[#FF6B00]" : "text-[#0B192C]"}`}>{value}</p>
  </div>
);

function Overview({ data }) {
  const { t } = useLang();
  if (!data) return null;
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Stat label={t("tab_listings")} value={data.rows.length} testid="stat-listings" />
        <Stat label={t("pending_req")} value={data.pending_requests} accent testid="stat-pending" />
        <Stat label={t("fee5")} value={eur(data.totals.fee_total)} testid="stat-fee" />
        <Stat label={t("earned")} value={eur(data.totals.net_total)} accent testid="stat-net" />
      </div>
      <div className="grid gap-3" data-testid="earnings-list">
        {data.rows.map((r) => (
          <div key={r.listing_id} className="rounded-2xl bg-white border border-slate-200 p-4 sm:p-5" data-testid={`earning-row-${r.listing_id}`}>
            <div className="flex flex-wrap items-center justify-between gap-2"><p className="font-semibold text-[#0B192C]">{r.title}</p><StatusBadge status={r.status} /></div>
            <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
              <div><p className="text-xs text-slate-500">{t("your_price")}</p><p className="font-bold">{eur(r.price_month)}</p></div>
              <div><p className="text-xs text-slate-500">{t("public_price")}</p><p className="font-bold">{eur(r.price_month_public)}</p></div>
              <div><p className="text-xs text-slate-500">{t("fee5")}</p><p className="font-bold text-red-600">-{eur(r.fee_month)}</p></div>
              <div><p className="text-xs text-slate-500">{t("net_month")}</p><p className="font-bold text-green-700">{eur(r.net_month)}</p></div>
            </div>
            {r.bookings > 0 && <p className="mt-3 text-xs text-slate-500">{t("bookings_n")}: {r.bookings} · {t("earned")}: <b>{eur(r.net_total)}</b></p>}
          </div>
        ))}
      </div>
    </div>
  );
}

function Listings({ items, reload }) {
  const { t } = useLang();
  const nav = useNavigate();
  const act = async (fn, msg) => { try { await fn(); toast.success(msg); reload(); } catch (e) { toast.error(errMsg(e)); } };
  if (!items.length) return <Empty text={t("no_listings")} testid="no-listings" />;
  return (
    <div className="grid gap-3" data-testid="my-listings">
      {items.map((l) => (
        <div key={l.listing_id} className="flex flex-col sm:flex-row gap-4 rounded-2xl bg-white border border-slate-200 p-3 sm:p-4" data-testid={`my-listing-${l.listing_id}`}>
          <div className="h-40 sm:h-24 sm:w-36 shrink-0 overflow-hidden rounded-xl bg-slate-100">{l.photos?.[0] && <img src={photoUrl(l.photos[0])} alt="" className="h-full w-full object-cover" />}</div>
          <div className="flex-1 min-w-0">
            <div className="flex flex-wrap items-center gap-2"><StatusBadge status={l.status} testid={`my-listing-status-${l.listing_id}`} /><span className="text-xs text-slate-500">{l.city}</span></div>
            <p className="mt-1 font-semibold text-[#0B192C] truncate">{l.title}</p>
            <p className="text-sm text-slate-600">{eur(l.price_month)} {t("per_month")} · {t("public_price")}: {eur(l.price_month_public)}</p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <button onClick={() => nav(`/dashboard/listings/${l.listing_id}`)} className="h-10 inline-flex items-center gap-1.5 rounded-full border border-slate-300 px-3 text-sm font-semibold" data-testid={`edit-listing-${l.listing_id}`}><Pencil className="h-4 w-4" />{t("edit")}</button>
            <Link to={`/listing/${l.listing_id}`} className="h-10 inline-flex items-center gap-1.5 rounded-full border border-slate-300 px-3 text-sm font-semibold" data-testid={`view-listing-${l.listing_id}`}><Eye className="h-4 w-4" />{t("view")}</Link>
            {l.status === "draft" && <button onClick={() => act(() => api.post(`/my/listings/${l.listing_id}/submit`), t("sent_review"))} className="h-10 inline-flex items-center gap-1.5 rounded-full bg-[#FF6B00] px-3 text-sm font-bold text-white" data-testid={`submit-listing-${l.listing_id}`}><Send className="h-4 w-4" />{t("submit_review")}</button>}
            <AlertDialog>
              <AlertDialogTrigger asChild><button className="h-10 w-10 grid place-items-center rounded-full border border-red-200 text-red-600" data-testid={`delete-listing-${l.listing_id}`}><Trash2 className="h-4 w-4" /></button></AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader><AlertDialogTitle>{t("confirm_delete")}</AlertDialogTitle></AlertDialogHeader>
                <AlertDialogFooter><AlertDialogCancel>{t("cancel")}</AlertDialogCancel><AlertDialogAction onClick={() => act(() => api.delete(`/my/listings/${l.listing_id}`), "OK")} data-testid="confirm-delete-listing">{t("delete")}</AlertDialogAction></AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        </div>
      ))}
    </div>
  );
}

export function BookingAmounts({ a, owner }) {
  const { t, pick } = useLang();
  return (
    <div className="rounded-xl bg-slate-50 p-3 text-sm grid gap-1">
      <div className="flex justify-between"><span>{t("rent")} ({a.months} {t("months")})</span><span>{eur(owner ? a.rent_owner : a.rent_public)}</span></div>
      {a.extras.map((e) => <div key={e.extra_id} className="flex justify-between text-slate-600"><span>{pick(e, "name")}</span><span>{eur(e.total)}</span></div>)}
      {owner ? (
        <>
          <div className="flex justify-between text-red-600"><span>{t("fee5")}</span><span>-{eur(a.owner_fee)}</span></div>
          <div className="flex justify-between border-t pt-1 font-extrabold text-green-700"><span>{t("net")}</span><span>{eur(a.owner_net)}</span></div>
        </>
      ) : (
        <div className="flex justify-between border-t pt-1 font-extrabold text-[#0B192C]"><span>{t("total")}</span><span>{eur(a.total_tenant)}</span></div>
      )}
    </div>
  );
}

function Requests({ items, reload }) {
  const { t } = useLang();
  const act = async (id, what) => { try { await api.post(`/bookings/${id}/${what}`); toast.success(t(what === "accept" ? "s_accepted" : "s_rejected")); reload(); } catch (e) { toast.error(errMsg(e)); } };
  if (!items.length) return <Empty text={t("no_requests")} testid="no-requests" />;
  return (
    <div className="grid gap-3 md:grid-cols-2" data-testid="requests-list">
      {items.map((b) => (
        <div key={b.booking_id} className="rounded-2xl bg-white border border-slate-200 p-4 sm:p-5 space-y-3" data-testid={`request-${b.booking_id}`}>
          <div className="flex items-center justify-between gap-2"><p className="font-semibold text-[#0B192C]">{b.listing_title}</p><StatusBadge status={b.status} testid={`request-status-${b.booking_id}`} /></div>
          <p className="text-sm text-slate-600"><b>{b.tenant_name}</b> · {b.tenant_email}</p>
          <div className="flex flex-wrap gap-3 text-sm text-slate-600"><span className="flex items-center gap-1"><CalendarDays className="h-4 w-4" />{fmtDate(b.check_in)} → {fmtDate(b.check_out)}</span><span className="flex items-center gap-1"><Users className="h-4 w-4" />{b.guests}</span></div>
          {b.message && <p className="rounded-xl border border-slate-200 p-3 text-sm italic text-slate-600">"{b.message}"</p>}
          <BookingAmounts a={b.amounts} owner />
          {b.status === "pending" && (
            <div className="flex gap-2">
              <button onClick={() => act(b.booking_id, "accept")} className="h-11 flex-1 inline-flex items-center justify-center gap-1.5 rounded-full bg-green-600 font-bold text-white hover:bg-green-700" data-testid={`accept-${b.booking_id}`}><Check className="h-4 w-4" />{t("accept")}</button>
              <button onClick={() => act(b.booking_id, "reject")} className="h-11 flex-1 inline-flex items-center justify-center gap-1.5 rounded-full border border-red-300 font-bold text-red-600 hover:bg-red-50" data-testid={`reject-${b.booking_id}`}><X className="h-4 w-4" />{t("reject")}</button>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

export default function OwnerDashboard() {
  const { t } = useLang();
  const { user } = useAuth();
  const [sp, setSp] = useSearchParams();
  const tab = sp.get("tab") || "overview";
  const [earn, setEarn] = useState(null);
  const [listings, setListings] = useState([]);
  const [reqs, setReqs] = useState([]);
  const load = useCallback(() => {
    api.get("/my/earnings").then((r) => setEarn(r.data));
    api.get("/my/listings").then((r) => setListings(r.data));
    api.get("/my/requests").then((r) => setReqs(r.data));
  }, []);
  useEffect(load, [load]);
  const pending = reqs.filter((r) => r.status === "pending").length;
  return (
    <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-8 sm:py-12" data-testid="owner-dashboard">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.18em] text-[#FF6B00]">{t(`r_${user.role}`)}{user.company_name ? ` · ${user.company_name}` : ""}</p>
          <h1 className="mt-1 text-3xl sm:text-4xl font-extrabold tracking-tight text-[#0B192C]">{t("dash_title")}</h1>
        </div>
        <Link to="/dashboard/listings/new" className="h-12 inline-flex items-center gap-2 rounded-full bg-[#FF6B00] px-5 font-bold text-white hover:bg-[#E05A00]" data-testid="new-listing-btn"><Plus className="h-5 w-5" />{t("new_listing")}</Link>
      </div>
      <Tabs value={tab} onValueChange={(v) => setSp({ tab: v })} className="mt-8">
        <TabsList className="h-12 w-full sm:w-auto grid grid-cols-3 sm:inline-flex rounded-full bg-white border border-slate-200 p-1">
          <TabsTrigger value="overview" className="rounded-full h-10 data-[state=active]:bg-[#142B5C] data-[state=active]:text-white" data-testid="tab-overview">{t("tab_overview")}</TabsTrigger>
          <TabsTrigger value="listings" className="rounded-full h-10 data-[state=active]:bg-[#142B5C] data-[state=active]:text-white" data-testid="tab-listings">{t("tab_listings")}</TabsTrigger>
          <TabsTrigger value="requests" className="rounded-full h-10 data-[state=active]:bg-[#142B5C] data-[state=active]:text-white" data-testid="tab-requests">{t("tab_requests")}{pending > 0 && <span className="ml-1.5 rounded-full bg-[#FF6B00] px-1.5 text-[10px] text-white">{pending}</span>}</TabsTrigger>
        </TabsList>
        <TabsContent value="overview" className="mt-6"><Overview data={earn} /></TabsContent>
        <TabsContent value="listings" className="mt-6"><Listings items={listings} reload={load} /></TabsContent>
        <TabsContent value="requests" className="mt-6"><Requests items={reqs} reload={load} /></TabsContent>
      </Tabs>
    </div>
  );
}
